import { useCallback, useEffect, useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";

/** 打开时监听外部点击与 Escape，关闭浮层。 */
export function useDismiss(ref: RefObject<HTMLElement | null>, open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, onClose, ref]);
}

/** 浮层面板与视口边留的距离（px），同 space-2。 */
const VIEWPORT_EDGE = 8;

/** 浮层面板按触发元素的位置相对视口定位（position: fixed），不会被表格、主区这类会滚动的容器裁掉。
 * 下方放不下而上方更宽时向上展开。外面滚动或窗口改变大小时跟着触发元素重新定位，触发元素滚出可见区域才关闭：
 * 打开时聚焦面板里的输入框可能让容器滚一下，不能因此把刚打开的面板关掉。面板自己里面滚动不算。
 * ref 是包住触发元素与面板的容器，面板是它的最后一个子元素：面板脱离文档流，容器的大小就是触发元素的大小。
 * 面板至少与触发元素同宽，按内容变宽（#240：与触发元素同宽时，窄触发器里的选项会折行错位），但不越过视口：
 * align 指定的这一侧放不下整个面板而另一侧放得下时翻到另一侧（靠右的触发元素弹出宽面板，例如时间范围的日历），
 * 两侧都放不下时取更宽的一侧，最宽到离视口边 VIEWPORT_EDGE 处，放不下的内容由组件自己截断；下拉选择另由 CSS 限在 modal-w。
 * 返回值并进面板的 style。 */
export function useFloating(ref: RefObject<HTMLElement | null>, open: boolean, align: "start" | "end" | undefined, onClose: () => void): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties>();
  useLayoutEffect(() => {
    if (!open || !ref.current) {
      setStyle(undefined);
      return;
    }
    const place = () => {
      const anchor = ref.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      // 用可见区域的宽高而不是 innerWidth/innerHeight：页面有经典滚动条时后者多算了滚动条的宽度
      const vw = document.documentElement.clientWidth || window.innerWidth, vh = document.documentElement.clientHeight || window.innerHeight;
      if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) {
        onClose();
        return;
      }
      const below = vh - r.bottom, above = r.top;
      // 对齐的那条边已经在视口外时这一侧算放不下（触发元素有一截在屏幕外），不能把面板对齐过去
      const startRoom = r.left >= 0 ? vw - r.left - VIEWPORT_EDGE : -1, endRoom = r.right <= vw ? r.right - VIEWPORT_EDGE : -1;
      const panel = anchor.lastElementChild;
      const need = Math.max(r.width, panel instanceof HTMLElement ? contentWidth(panel) : 0);
      const end = fitsEnd(align === "end", need, startRoom, endRoom);
      setStyle({
        position: "fixed", minWidth: r.width, maxWidth: Math.max(r.width, end ? endRoom : startRoom),
        ...(end ? { left: "auto", right: vw - r.right } : { left: r.left, right: "auto" }),
        ...(below < 240 && above > below ? { top: "auto", bottom: vh - r.top, marginTop: 0, marginBottom: "var(--space-1)" } : { top: r.bottom, bottom: "auto" }),
      });
    };
    place();
    // resize 与窗口级 scroll 的 target 是 window，不是节点，不能交给 contains
    const onMove = (e: Event) => { if (!(e.target instanceof Node && ref.current?.contains(e.target))) place(); };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => { window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove); };
  }, [open, align, onClose, ref]);
  return style;
}

/** 面板靠右对齐还是靠左：先按 preferEnd 要的那一侧，放不下而另一侧放得下就翻过去，都放不下取更宽的一侧。 */
function fitsEnd(preferEnd: boolean, need: number, startRoom: number, endRoom: number): boolean {
  const room = preferEnd ? endRoom : startRoom, other = preferEnd ? startRoom : endRoom;
  if (need <= room) return preferEnd;
  if (need <= other) return !preferEnd;
  return endRoom > startRoom;
}

/** 面板内容不换行、不受宽度限制时的宽度：临时放开再量，量完恢复原值（React 只写自己改过的样式，不会替我们恢复）。 */
function contentWidth(panel: HTMLElement): number {
  const { width, maxWidth } = panel.style;
  panel.style.width = "max-content";
  panel.style.maxWidth = "none";
  const w = panel.offsetWidth;
  panel.style.width = width;
  panel.style.maxWidth = maxWidth;
  return w;
}

export interface OpenControl {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function useControllableOpen(props: OpenControl): [boolean, (v: boolean) => void] {
  const [inner, setInner] = useState(!!props.defaultOpen);
  const open = props.open ?? inner;
  const { onOpenChange, open: controlled } = props;
  const set = useCallback((v: boolean) => { onOpenChange?.(v); if (controlled == null) setInner(v); }, [onOpenChange, controlled]);
  return [open, set];
}


/** 打开时按 Escape 关闭。Modal、Drawer 共用。 */
export function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}
